import type { WellCorrelationWidgetState, WellCorrelationWidgetStateParams } from '@go-widgets/well-logs-widget';
import { action, makeObservable, observable } from 'mobx';

import type { SizeBoundaries, TWidgetOptions } from 'src/entities/widget/WidgetEntity';
import { WidgetWithEditMode } from 'src/entities/widget/WidgetWithEditMode';
import { requireService } from 'src/packages/di';

interface WellCorrelationWidgetOptions {
  type: string;
  stateParams: Partial<WellCorrelationWidgetStateParams>;
  wellId: number | null;
  restoredFromSnapshot?: boolean;
}

export type TWellCorrelationSerializedInternalState = {
  stateParams: Partial<WellCorrelationWidgetStateParams>;
  wellId: number | null;
};

export class WellCorrelationWidgetEntity extends WidgetWithEditMode {
  readonly sizeBoundaries: SizeBoundaries = {
    minInPixels: {
      w: 300,
      h: 200,
    },
    maxInPixels: null,
    min: null,
    max: {
      w: 256,
      h: 100,
    },
  };

  readonly restoredFromTabSnapshot: boolean;

  @observable state: WellCorrelationWidgetState | null = null;
  @observable type: string;
  @observable isEditMode: boolean = false;
  @observable wellId: number | null;
  @observable.ref stateParams: Partial<WellCorrelationWidgetStateParams>;
  @observable inActiveTab: boolean = false;
  @observable wellboreName: string | null = null;

  private disposeHostStore: (() => void) | null = null;

  constructor(
    { type, stateParams, wellId, restoredFromSnapshot: restoredFromTabSnapshot }: WellCorrelationWidgetOptions,
    widgetOptions: TWidgetOptions,
    private readonly i18 = requireService('i18'),
  ) {
    super(widgetOptions);

    this.type = type;
    this.wellId = wellId;
    this.stateParams = stateParams;
    this.restoredFromTabSnapshot = restoredFromTabSnapshot ?? false;

    makeObservable(this);
  }

  getNameT(): string {
    return 'widgets:WellCorrelation.title';
  }

  @action.bound
  setWellId(wellId: number | null): void {
    this.wellId = wellId;
  }

  @action.bound
  setWellboreName(name: string): void {
    this.wellboreName = name;
  }

  @action.bound
  setStateParams(stateParams: Partial<WellCorrelationWidgetStateParams>): void {
    this.stateParams = stateParams;
  }

  @action.bound
  setEditMode(value: boolean): void {
    this.isEditMode = value;

    if (value && !this.isFullscreen) {
      this.setFullscreen(true);
    }
  }

  @action.bound
  setFullscreen(isFullscreen: boolean): void {
    this.isFullscreen = isFullscreen;
    this.state?.setAppParams({ fullScreen: isFullscreen });
  }

  get isControlWidget(): boolean {
    return false;
  }

  get title(): string {
    return this.i18.t(this.getNameT());
  }

  @action.bound
  setState(state: WellCorrelationWidgetState | null): void {
    this.state = state;
  }

  @action.bound
  setInActiveTab(value: boolean): void {
    this.inActiveTab = value;
  }

  @action.bound
  setDisposeHostStore(dispose: (() => void) | null): void {
    this.disposeHostStore = dispose;
  }

  override destroy() {
    this.disposeHostStore?.();
    this.disposeHostStore = null;
    this.state?.destroy();
  }
}
